import React from 'react'
import { ambient } from '@aw_products/ambient-weather-common'
import { useParams } from 'react-router-dom'
import { HsThreshold } from './'
import bindDeviceActions from './redux/bindDeviceActions'
import PropTypes from 'prop-types'
const { HST } = ambient

function HeatStressGuidelines ({ device, actions }) {
  const { guideline, zone, level } = useParams()

  // Detect if guideline is hard-coded or a deviceId
  const isHardCodedGuideline = guideline && HST.guidelines[guideline]
  const isCustomGuideline = guideline && !isHardCodedGuideline
  const deviceId = isCustomGuideline ? guideline : null

  // State for fetched device
  const [fetchedDevice, setFetchedDevice] = React.useState(null)

  // Fetch device when needed
  React.useEffect(() => {
    if (isCustomGuideline && deviceId) {
      const cachedDevice = device.deviceCache[deviceId]
      if (cachedDevice) {
        setFetchedDevice(cachedDevice)
      } else {
        actions.getDevice(deviceId)
      }
    }
  }, [isCustomGuideline, deviceId, actions, device.deviceCache])

  // Update fetchedDevice when cache updates
  React.useEffect(() => {
    if (isCustomGuideline && deviceId && device.deviceCache[deviceId]) {
      setFetchedDevice(device.deviceCache[deviceId])
    }
  }, [isCustomGuideline, deviceId, device.deviceCache])

  // Track loading/error states
  const isPending = isCustomGuideline && device.getDeviceIdsPending?.includes(deviceId)
  const hasError = isCustomGuideline && device.getDeviceFailures?.includes(deviceId)

  let title = 'Heat Stress Guidelines'
  let show = null

  if (isHardCodedGuideline) {
    // Existing hard-coded guideline logic
    let guide = HST.guidelines[guideline]
    if (guide) {
      title = guide.label
      if (level) {
        guide = guide.guidelines.find(g => g.label === level)
        title += ` - ${guide.label}`
      } else {
        guide = guide.guidelines[0]
      }
      const z = zone && guide.zones.find(z => z.label === zone)
      if (z) {
        show = (
          <div>
            <HsThreshold zone={z} guideline={guide} />
            <a href={guide.source} target='_blank' rel='noopener noreferrer'>Learn more</a>
          </div>
        )
      } else {
        show = (
          <div className='guideline'>
            {guide.zones.map(z => (
              <HsThreshold key={z.label} zone={z} guideline={guide} />
            ))}
          </div>
        )
      }
    }
  } else if (isCustomGuideline) {
    if (isPending) {
      show = <div>Loading custom guideline...</div>
    } else if (hasError) {
      show = (
        <div className='error'>
          Failed to load custom guideline. The device may not exist or you may not have access.
        </div>
      )
    } else if (fetchedDevice) {
      const customGuideline = fetchedDevice.settings?.hst?.customGuideline

      if (customGuideline && customGuideline.zones && customGuideline.zones.length > 0) {
        title = `Custom Heat Stress Guideline - ${fetchedDevice.info?.name || 'Unknown Device'}`

        const zones = zone
          ? customGuideline.zones.filter(z => z.label === zone)
          : customGuideline.zones

        if (zones.length > 0) {
          show = (
            <div className='guideline'>
              {zones.map(z => (
                <HsThreshold key={z._id} zone={z} guideline={customGuideline} />
              ))}
            </div>
          )
        } else {
          show = <div>No zone found with label "{zone}"</div>
        }
      } else {
        show = <div>This device has no custom heat stress guideline configured.</div>
      }
    }
  }


  return (
    <div className='device-heat-stress-guidelines'>
      <header className='main'>
        <h1>
          {title}
        </h1>
      </header>
      {show && (
        <div className='block'>{show}</div>
      )}
    </div>
  )
};

HeatStressGuidelines.propTypes = {
  device: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
}

HeatStressGuidelines.displayName = 'HeatStressGuidelines'

export default bindDeviceActions(HeatStressGuidelines)
